/**
 * Plausible Analytics Component
 *
 * Privacy-focused analytics that only tracks public pages.
 * NO tracking in backoffice for user privacy.
 *
 * Plausible is GDPR compliant, doesn't use cookies,
 * and is lightweight (< 1 KB).
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface PlausibleProps {
  domain?: string;
  plausibleUrl?: string;
}

export function PlausibleAnalytics({ domain, plausibleUrl }: PlausibleProps) {
  const location = useLocation();
  const scriptLoadedRef = useRef(false);
  const scriptFailedRef = useRef(false);

  useEffect(() => {
    // Check if Plausible is configured
    const plausibleDomain = domain || import.meta.env.VITE_PLAUSIBLE_DOMAIN;
    if (!plausibleDomain) {
      // Skip loading entirely if not configured
      if (import.meta.env.DEV) {
        console.warn('Plausible Analytics: VITE_PLAUSIBLE_DOMAIN not configured. Analytics disabled.');
      }
      return;
    }

    // Define routes to exclude from tracking (auth + backoffice)
    const excludedRoutes = [
      '/login', '/register', '/forgot-password', '/reset-password', '/accept-invite', // Auth routes
      '/dashboard', '/content', '/presentations', '/company', '/admin', // Backoffice routes
      '/chat', '/profile', '/settings', '/users', '/library', // Additional backoffice routes
      '/access-denied' // Error pages
    ];

    // Check if current route should be excluded
    const isExcludedRoute = excludedRoutes.some(route =>
      location.pathname === route || location.pathname.startsWith(route + '/')
    );

    if (isExcludedRoute) {
      return; // Don't track excluded routes
    }

    // Don't attempt to load script if it previously failed
    if (scriptFailedRef.current) {
      return;
    }

    let script: HTMLScriptElement | null = null;

    try {
      // Load Plausible script dynamically with enhanced error handling
      script = document.createElement('script');
      script.defer = true;
      script.dataset.domain = plausibleDomain;
      script.src = `${plausibleUrl || import.meta.env.VITE_PLAUSIBLE_URL || 'https://plausible.io'}/js/script.outbound-links.js`; // Using outbound-links script for better tracking

      // Handle script load success
      script.onload = () => {
        scriptLoadedRef.current = true;
      };

      // Handle script load errors gracefully (e.g., ad blockers)
      script.onerror = () => {
        scriptFailedRef.current = true;
        // Silently fail - ad blockers blocking analytics scripts is expected behavior
        // No console errors to avoid spam
      };

      document.head.appendChild(script);
    } catch (error) {
      // Handle any synchronous errors during script creation
      scriptFailedRef.current = true;
      // Silently fail - don't log errors
    }

    // Cleanup script on unmount
    return () => {
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
      scriptLoadedRef.current = false;
    };
  }, [location.pathname, domain, plausibleUrl]);

  // Track page views when route changes
  useEffect(() => {
    // Check if Plausible is configured
    const plausibleDomain = domain || import.meta.env.VITE_PLAUSIBLE_DOMAIN;
    if (!plausibleDomain) {
      return;
    }

    // Define routes to exclude from tracking (auth + backoffice)
    const excludedRoutes = [
      '/login', '/register', '/forgot-password', '/reset-password', '/accept-invite',
      '/dashboard', '/content', '/presentations', '/company', '/admin',
      '/chat', '/profile', '/settings', '/users', '/library',
      '/access-denied'
    ];

    // Check if current route should be excluded
    const isExcludedRoute = excludedRoutes.some(route =>
      location.pathname === route || location.pathname.startsWith(route + '/')
    );

    if (isExcludedRoute) {
      return; // Don't track excluded routes
    }

    // Only track if script loaded successfully and hasn't failed
    if (scriptLoadedRef.current && !scriptFailedRef.current && window.plausible) {
      try {
        window.plausible('pageview', {
          u: window.location.href
        });
      } catch (error) {
        // Silently fail - don't log errors for user privacy
      }
    }
  }, [location.pathname, domain]);

  return null; // This component doesn't render anything
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    plausible?: (event: string, options?: { u?: string; props?: Record<string, unknown> }) => void;
  }
}

export default PlausibleAnalytics;
